import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PiggyBank, Loader2, Plus, X, Building2, CalendarDays } from "lucide-react";

const cellKey = (p, v) => `${p}|||${v}`;
const slug = (s) => (s || "").replace(/[^a-zA-Z0-9]+/g, "-");

/**
 * Governed editor for planned marketing budgets (project × vertical).
 *
 * PART 3 (3.6) upgrades:
 *  - Verticals are the admin-editable TAXONOMY project verticals — add/delete
 *    persists globally via /api/taxonomy/verticals.
 *  - Actuals auto-populate from the backend (imported / submitted invoices) and
 *    are shown inline against every planned cell — never typed.
 *  - Project-wise (editable planned grid) ↔ Month-wise (read-only actuals pacing)
 *    view toggle.
 *
 * Spare Budget = Planned − Spent, and drives the Spends-by-Vertical red/green chart.
 * Leader / Admin only (enforced by the backend).
 */
export function PlannedBudgetDialog({ onSaved, trigger }) {
  const { money } = useCurrency();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyVert, setBusyVert] = useState(false);
  const [viewMode, setViewMode] = useState("project"); // "project" | "month"
  const [projects, setProjects] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [values, setValues] = useState({});     // cellKey -> string amount (planned)
  const [original, setOriginal] = useState({});
  const [actuals, setActuals] = useState({});    // cellKey -> spent (number)
  const [monthActuals, setMonthActuals] = useState([]);
  const [editable, setEditable] = useState(false);

  // add-vertical inline state
  const [newVert, setNewVert] = useState("");
  // delete-vertical reassign flow
  const [reassign, setReassign] = useState(null); // { name, count, options, message }
  const [reassignTo, setReassignTo] = useState("");

  const load = () => {
    setLoading(true);
    api.plannedBudgets().then((d) => {
      const map = {};
      (d.rows || []).forEach((r) => { map[cellKey(r.project, r.vertical)] = String(r.amount || 0); });
      setProjects(d.projects || []);
      setVerticals(d.verticals || []);
      setValues(map);
      setOriginal(map);
      setActuals(d.actuals || {});
      setMonthActuals(d.month_actuals || []);
      setEditable(!!d.editable);
    }).catch(() => toast.error("Could not load planned budgets"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open) load(); }, [open]);

  const setCell = (p, v, val) => setValues((s) => ({ ...s, [cellKey(p, v)]: val }));
  const cellVal = (p, v) => values[cellKey(p, v)] ?? "0";
  const actualOf = (p, v) => Number(actuals[cellKey(p, v)] || 0);
  const rowPlanned = (p) => verticals.reduce((s, v) => s + (parseFloat(cellVal(p, v)) || 0), 0);
  const rowActual = (p) => verticals.reduce((s, v) => s + actualOf(p, v), 0);
  const vertPlanned = (v) => projects.reduce((s, p) => s + (parseFloat(cellVal(p, v)) || 0), 0);
  const vertActual = (v) => projects.reduce((s, p) => s + actualOf(p, v), 0);

  const grandPlanned = useMemo(
    () => projects.reduce((s, p) => s + verticals.reduce((a, v) => a + (parseFloat(values[cellKey(p, v)]) || 0), 0), 0),
    [values, projects, verticals]
  );
  const grandActual = useMemo(
    () => projects.reduce((s, p) => s + verticals.reduce((a, v) => a + actualOf(p, v), 0), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actuals, projects, verticals]
  );

  const changed = () => {
    const out = [];
    projects.forEach((p) => verticals.forEach((v) => {
      const k = cellKey(p, v);
      const nv = parseFloat(values[k]) || 0;
      const ov = parseFloat(original[k]) || 0;
      if (nv !== ov) out.push({ project: p, vertical: v, amount: nv });
    }));
    return out;
  };

  const save = async () => {
    const diffs = changed();
    if (!diffs.length) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      for (const d of diffs) {
        await api.savePlannedBudget({ ...d, reason: "planned budget update" });
      }
      toast.success(`Saved ${diffs.length} planned budget${diffs.length > 1 ? "s" : ""}`);
      onSaved && onSaved();
      setOriginal({ ...values });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save planned budgets");
    } finally { setSaving(false); }
  };

  const addVertical = async () => {
    const name = newVert.trim();
    if (!name) return;
    if (verticals.includes(name)) { toast.error(`"${name}" already exists`); return; }
    setBusyVert(true);
    try {
      await api.addVertical({ name, spend_type: "Project", label: name });
      toast.success(`Added vertical "${name}"`);
      setNewVert("");
      load();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add vertical");
    } finally { setBusyVert(false); }
  };

  const doRemove = async (name, target = null) => {
    setBusyVert(true);
    try {
      const res = await api.removeVertical(name, target);
      toast.success(target
        ? `Removed "${name}" · reassigned ${res.reassigned} invoice(s) to "${target}"`
        : `Removed vertical "${name}"`);
      setReassign(null); setReassignTo("");
      load();
      onSaved && onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && detail.code === "reassign_required") {
        setReassign({ name, count: detail.count, options: detail.options || [], message: detail.message });
        setReassignTo("");
      } else {
        toast.error((detail && detail.message) || detail || "Could not remove vertical");
      }
    } finally { setBusyVert(false); }
  };

  const monthGrandTotal = useMemo(
    () => monthActuals.reduce((s, m) => s + (m.total || 0), 0),
    [monthActuals]
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" data-testid="planned-budgets-open">
            <PiggyBank className="mr-1.5 h-4 w-4" /> Set planned budgets
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-5xl" data-testid="planned-budgets-dialog">
        <DialogHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle>Planned budgets</DialogTitle>
              <DialogDescription>
                Set the planned marketing budget per project × vertical (in ₹). Actuals are pulled
                live from invoices. Spare = Planned − Spent.
              </DialogDescription>
            </div>
            <ToggleGroup type="single" value={viewMode} onValueChange={(v) => v && setViewMode(v)}
              className="rounded-lg border bg-[hsl(var(--surface-2))] p-0.5" data-testid="planned-view-toggle">
              <ToggleGroupItem value="project" className="h-7 gap-1.5 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="planned-view-project">
                <Building2 className="h-3.5 w-3.5" /> Project-wise
              </ToggleGroupItem>
              <ToggleGroupItem value="month" className="h-7 gap-1.5 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="planned-view-month">
                <CalendarDays className="h-3.5 w-3.5" /> Month-wise
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </DialogHeader>

        {/* Add vertical (admin) — persists to the global taxonomy */}
        {editable && viewMode === "project" && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-[hsl(var(--surface-2))] px-3 py-2" data-testid="planned-add-vertical">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Add project vertical</span>
            <Input value={newVert} onChange={(e) => setNewVert(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addVertical(); } }}
              placeholder="e.g. Events" className="h-8 w-44" data-testid="planned-add-vertical-input" />
            <Button size="sm" variant="outline" className="h-8" disabled={busyVert || !newVert.trim()} onClick={addVertical} data-testid="planned-add-vertical-btn">
              <Plus className="mr-1 h-3.5 w-3.5" /> Add
            </Button>
            <span className="text-[11px] text-muted-foreground">Verticals are shared across the whole app.</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground" data-testid="planned-budgets-loading">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading planned budgets…
          </div>
        ) : viewMode === "project" ? (
          <div className="table-scroll" style={{ maxHeight: "52vh" }}>
            <table className="w-full border-collapse text-sm" data-testid="planned-budgets-table">
              <thead>
                <tr>
                  <th className="th-sticky th-sticky-left px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Project</th>
                  {verticals.map((v) => (
                    <th key={v} className="th-sticky px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        {v}
                        {editable && (
                          <button type="button" onClick={() => doRemove(v)} disabled={busyVert}
                            className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-[hsl(var(--danger)/0.12)] hover:text-[hsl(var(--danger))] disabled:opacity-40"
                            title={`Remove ${v}`} data-testid={`planned-del-vertical-${slug(v)}`}>
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    </th>
                  ))}
                  <th className="th-sticky px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Total</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p, i) => {
                  const rp = rowPlanned(p); const ra = rowActual(p);
                  return (
                    <tr key={p} className={"border-b last:border-0 " + (i % 2 ? "row-zebra" : "")} data-testid={`planned-row-${slug(p)}`}>
                      <td className="td-sticky-left px-3 py-1.5 font-medium">{p}</td>
                      {verticals.map((v) => {
                        const act = actualOf(p, v);
                        const plan = parseFloat(cellVal(p, v)) || 0;
                        const over = plan > 0 && act > plan;
                        return (
                          <td key={v} className="px-1 py-1 align-top">
                            <Input
                              type="number" min="0" value={cellVal(p, v)}
                              onChange={(e) => setCell(p, v, e.target.value)}
                              className="h-8 w-24 text-right tabular-nums"
                              data-testid={`planned-input-${slug(p)}-${slug(v)}`}
                            />
                            <div className={"mt-0.5 pr-1 text-right text-[10px] tabular-nums " + (over ? "text-[hsl(var(--danger))]" : "text-muted-foreground")}
                              data-testid={`planned-actual-${slug(p)}-${slug(v)}`}>
                              {act > 0 ? `spent ${money(act)}` : "—"}
                            </div>
                          </td>
                        );
                      })}
                      <td className="px-3 py-1.5 text-right align-top">
                        <div className="font-medium tabular-nums text-foreground">{money(rp)}</div>
                        <div className="mt-0.5 text-[10px] tabular-nums text-muted-foreground">spent {money(ra)}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 bg-[hsl(var(--surface-2))]">
                  <td className="td-sticky-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Planned / Spent</td>
                  {verticals.map((v) => (
                    <td key={v} className="px-2 py-2 text-right align-top">
                      <div className="text-xs font-semibold tabular-nums">{money(vertPlanned(v))}</div>
                      <div className="mt-0.5 text-[10px] tabular-nums text-muted-foreground">{money(vertActual(v))}</div>
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right align-top">
                    <div className="text-xs font-semibold tabular-nums" data-testid="planned-foot-planned">{money(grandPlanned)}</div>
                    <div className="mt-0.5 text-[10px] tabular-nums text-muted-foreground" data-testid="planned-foot-actual">{money(grandActual)}</div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="table-scroll" style={{ maxHeight: "52vh" }} data-testid="planned-month-view">
            <p className="mb-2 text-[11px] text-muted-foreground">Actual spend by month × vertical (project spends), auto-populated from invoices.</p>
            {monthActuals.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">No spend recorded yet.</div>
            ) : (
              <table className="w-full border-collapse text-sm" data-testid="planned-month-table">
                <thead>
                  <tr>
                    <th className="th-sticky th-sticky-left px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Month</th>
                    {verticals.map((v) => (
                      <th key={v} className="th-sticky px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{v}</th>
                    ))}
                    <th className="th-sticky px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {monthActuals.map((m, i) => (
                    <tr key={m.month} className={"border-b last:border-0 " + (i % 2 ? "row-zebra" : "")} data-testid={`planned-month-row-${slug(m.month)}`}>
                      <td className="td-sticky-left px-3 py-1.5 font-medium">{m.month}</td>
                      {verticals.map((v) => (
                        <td key={v} className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{(m.values[v] || 0) > 0 ? money(m.values[v]) : "—"}</td>
                      ))}
                      <td className="px-3 py-1.5 text-right font-medium tabular-nums">{money(m.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 bg-[hsl(var(--surface-2))]">
                    <td className="td-sticky-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Total spent</td>
                    {verticals.map((v) => (
                      <td key={v} className="px-2 py-2 text-right text-xs font-semibold tabular-nums">
                        {money(monthActuals.reduce((s, m) => s + (m.values[v] || 0), 0))}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums" data-testid="planned-month-grand">{money(monthGrandTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        )}

        <DialogFooter className="sm:justify-between">
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span>Total planned:&nbsp;<span className="font-semibold tabular-nums text-foreground" data-testid="planned-grand-total">{money(grandPlanned)}</span></span>
            <span>Total spent:&nbsp;<span className="font-semibold tabular-nums text-foreground" data-testid="planned-grand-actual">{money(grandActual)}</span></span>
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="planned-budgets-cancel">Close</Button>
            {viewMode === "project" && (
              <Button onClick={save} disabled={saving || loading || !editable} data-testid="planned-budgets-save">
                {saving ? "Saving…" : "Save"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Reassign-before-delete guard for a vertical that still carries invoices */}
      <Dialog open={!!reassign} onOpenChange={(o) => !o && !busyVert && (setReassign(null), setReassignTo(""))}>
        <DialogContent className="max-w-md" data-testid="planned-reassign-dialog">
          <DialogHeader>
            <DialogTitle>Reassign invoices first</DialogTitle>
            <DialogDescription>
              {reassign?.message || `${reassign?.count} invoice(s) are tagged "${reassign?.name}". Choose a vertical to move them to before removing it.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Move invoices to</label>
            <Select value={reassignTo} onValueChange={setReassignTo}>
              <SelectTrigger data-testid="planned-reassign-select"><SelectValue placeholder="Select a vertical" /></SelectTrigger>
              <SelectContent>
                {(reassign?.options || []).map((o) => (
                  <SelectItem key={o} value={o} data-testid={`planned-reassign-opt-${slug(o)}`}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setReassign(null); setReassignTo(""); }} data-testid="planned-reassign-cancel">Cancel</Button>
            <Button variant="destructive" disabled={busyVert || !reassignTo} onClick={() => doRemove(reassign.name, reassignTo)} data-testid="planned-reassign-confirm">
              {busyVert ? "Removing…" : "Reassign & remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
